import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogIn, MapPinned, ShieldCheck, Plus } from "lucide-react";
import MapCanvas from "@/components/MapCanvas";
import BottomSheet from "@/components/BottomSheet";
import LoginDialog from "@/components/LoginDialog";
import InfrastructureList from "@/components/InfrastructureList";
import CategoryFilter from "@/components/CategoryFilter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";

const NO_CAT = "Ohne Kategorie";
const catKey = (c) => c || NO_CAT;

function within(b, it) {
  if (it.latitude == null || it.longitude == null) return false;
  return (
    it.latitude <= b.north &&
    it.latitude >= b.south &&
    it.longitude <= b.east &&
    it.longitude >= b.west
  );
}

function useIsDesktop() {
  const query = "(min-width: 1024px)";
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const handler = (e) => setIsDesktop(e.matches);
    m.addEventListener("change", handler);
    return () => m.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function MapView() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFocus = location.state?.focus || null;
  const { authed, logout } = useAuth();
  const { items, rootTitle, loading } = useData();
  const isDesktop = useIsDesktop();

  const [bounds, setBounds] = useState(null);
  const [selectedId, setSelectedId] = useState(initialFocus?.permid || null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [focus] = useState(initialFocus);
  const [fitNonce, setFitNonce] = useState(0);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("go_view_mode") || "category"
  );
  const [hidden, setHidden] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("go_hidden_cats") || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    localStorage.setItem("go_hidden_cats", JSON.stringify([...hidden]));
  }, [hidden]);

  useEffect(() => {
    localStorage.setItem("go_view_mode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (initialFocus) window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMoveEnd = useCallback((b) => setBounds(b), []);
  const handleSelect = useCallback((it) => navigate(`/item/${it.permid}`), [navigate]);

  const categoryOptions = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      const c = catKey(i.category);
      m[c] = (m[c] || 0) + 1;
    });
    return Object.keys(m)
      .sort((a, b) => a.localeCompare(b, "de"))
      .map((name) => ({ name, count: m[name] }));
  }, [items]);

  const singleActiveCategory = useMemo(() => {
    const active = categoryOptions.filter((o) => !hidden.has(o.name));
    return active.length === 1 && active[0].name !== NO_CAT ? active[0].name : null;
  }, [categoryOptions, hidden]);

  const mapItems = useMemo(
    () => items.filter((i) => !hidden.has(catKey(i.category))),
    [items, hidden]
  );

  const visible = useMemo(() => {
    if (!bounds) return mapItems.filter((i) => i.latitude != null);
    return mapItems.filter((i) => within(bounds, i));
  }, [mapItems, bounds]);

  useEffect(() => {
    document.title = `${rootTitle || "Weinwoche Wiesbaden 2026"} · Karte`;
  }, [rootTitle]);

  const toggleCat = (name) =>
    setHidden((prev) => {
      const n = new Set(prev);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  const showAll = () => setHidden(new Set());
  const hideAll = () => setHidden(new Set(categoryOptions.map((o) => o.name)));

  const goCreate = () =>
    navigate("/create", { state: { defaultCategory: singleActiveCategory } });
  const recenter = () => setFitNonce((n) => n + 1);

  const authButton = authed ? (
    <button
      onClick={() => setLogoutOpen(true)}
      data-testid="logout-button"
      className="glass flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-emerald-700 transition-transform hover:scale-[1.03] active:scale-[0.97]"
    >
      <ShieldCheck size={16} /> Angemeldet
    </button>
  ) : (
    <button
      onClick={() => setLoginOpen(true)}
      data-testid="login-button"
      className="flex items-center gap-2 rounded-2xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-500/30 transition-transform hover:scale-[1.03] active:scale-[0.97]"
    >
      <LogIn size={16} /> Anmelden
    </button>
  );

  const listNode = (
    <InfrastructureList
      items={visible}
      loading={loading}
      totalItems={mapItems.length}
      authed={authed}
      onSelect={handleSelect}
      onHover={setSelectedId}
      onRecenter={recenter}
      mode={viewMode}
    />
  );

  return (
    <div className="relative flex h-[100dvh] w-full overflow-hidden bg-slate-100">
      {/* Desktop sidebar */}
      {isDesktop && (
        <aside
          className="flex w-[384px] shrink-0 flex-col border-r border-slate-200 bg-white z-20"
          data-testid="desktop-sidebar"
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                <MapPinned size={12} /> Event Karte
              </p>
              <h1 className="truncate font-display text-xl font-bold leading-tight text-slate-900">
                {rootTitle || "Weinwoche Wiesbaden 2026"}
              </h1>
              <p className="mt-0.5 text-xs font-semibold uppercase tracking-widest text-slate-400">
                {visible.length} sichtbar
              </p>
            </div>
            {authed && (
              <button
                data-testid="add-item-button"
                onClick={goCreate}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary text-white shadow-lg shadow-amber-500/30 transition-transform hover:scale-[1.05] active:scale-[0.95]"
                aria-label="Objekt hinzufügen"
              >
                <Plus size={22} strokeWidth={2.6} />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">{listNode}</div>
        </aside>
      )}

      {/* Map area */}
      <div className="relative h-[100dvh] flex-1 lg:h-auto">
        <MapCanvas
          className="gomap absolute inset-0 z-0"
          items={mapItems}
          selectedId={selectedId}
          onMoveEnd={handleMoveEnd}
          onSelect={handleSelect}
          focus={focus}
          fitAll={fitNonce}
        />

        {/* Mobile title overlay (left) */}
        {!isDesktop && (
          <div className="glass pointer-events-auto absolute left-3 top-3 z-20 rounded-2xl px-4 py-2.5">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
              <MapPinned size={12} /> Event Karte
            </p>
            <h1 className="font-display text-lg font-bold leading-tight text-slate-900">
              {rootTitle || "Weinwoche Wiesbaden 2026"}
            </h1>
          </div>
        )}

        {/* Top-right controls (auth + filter) */}
        <div className="absolute right-3 top-3 z-[500] flex flex-col items-end gap-2">
          {authButton}
          <CategoryFilter
            options={categoryOptions}
            hidden={hidden}
            onToggle={toggleCat}
            onShowAll={showAll}
            onHideAll={hideAll}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </div>
      </div>

      {/* Mobile bottom sheet */}
      {!isDesktop && (
        <BottomSheet
          title="Infrastruktur"
          count={visible.length}
          canAdd={authed}
          onAdd={goCreate}
        >
          {listNode}
        </BottomSheet>
      )}

      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />

      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent data-testid="logout-confirm" className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Abmelden?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchtest du dich wirklich abmelden? Zum Anlegen und Bearbeiten musst
              du dich danach erneut anmelden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-confirm-button"
              onClick={() => {
                logout();
                setLogoutOpen(false);
              }}
            >
              Abmelden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
